import { MatrixEvent, MatrixEventEvent, Room, RoomEvent } from 'matrix-js-sdk/src/matrix';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { isMessengerRoom } from 'modules/Messenger/components/ConversationsList/utils';
import logger from 'utils/logging/faro';

const NOTIFICATION_SOUND_PATH = '/sounds/notification.mp3';
const MESSAGE_EVENT_TYPE = 'm.room.message';
const HANDLED_EVENT_IDS_CAP = 1000;
const DECRYPTED_MAX_AGE_MS = 30_000;

interface UseSoundNotificationsOptions {
  isMuted: boolean;
  mutedRoomIds?: Set<string>;
}

const isTabVisible = (): boolean => {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
};

const useSoundNotifications = ({ isMuted, mutedRoomIds }: UseSoundNotificationsOptions): void => {
  const { matrixClient } = useMatrixContext();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const handledEventIdsRef = useRef<Set<string>>(new Set());
  const location = useLocation();

  const isMutedRef = useRef(isMuted);
  const mutedRoomIdsRef = useRef(mutedRoomIds);
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    isMutedRef.current = isMuted;
    mutedRoomIdsRef.current = mutedRoomIds;
    pathnameRef.current = location.pathname;
  }, [isMuted, mutedRoomIds, location.pathname]);

  useEffect(() => {
    if (audioRef.current) return;
    try {
      audioRef.current = new Audio(NOTIFICATION_SOUND_PATH);
      audioRef.current.preload = 'auto';
    } catch (error) {
      logger.warn('useSoundNotifications: failed to initialize audio', error);
    }
  }, []);

  useEffect(() => {
    const myUserId = matrixClient.getUserId();
    const sessionStartTime = Date.now();

    const markHandled = (eventId: string | undefined): boolean => {
      if (!eventId) return false;
      if (handledEventIdsRef.current.has(eventId)) return false;
      handledEventIdsRef.current.add(eventId);
      if (handledEventIdsRef.current.size > HANDLED_EVENT_IDS_CAP) {
        const first = handledEventIdsRef.current.values().next().value;
        if (first) handledEventIdsRef.current.delete(first);
      }
      return true;
    };

    const shouldPlay = (event: MatrixEvent, room: Room | undefined, isLive: boolean): boolean => {
      if (!room) return false;
      if (!isLive) return false;
      if ((event.getTs() ?? 0) < sessionStartTime) return false;
      if (event.getType() !== MESSAGE_EVENT_TYPE) return false;
      if ((event.getContent()['m.relates_to'] as { rel_type?: string } | undefined)?.rel_type === 'm.replace') {
        return false;
      }
      if (event.getSender() === myUserId) return false;
      if (isMutedRef.current) return false;
      if (mutedRoomIdsRef.current?.has(room.roomId)) return false;
      if (!isMessengerRoom(room)) return false;
      if (isTabVisible() && pathnameRef.current.startsWith(`/messenger/${room.roomId}`)) return false;
      return markHandled(event.getId());
    };

    const play = () => {
      audioRef.current?.play().catch(() => undefined);
    };

    const handleTimeline = (
      event: MatrixEvent,
      room: Room | undefined,
      toStartOfTimeline?: boolean,
      _removed?: boolean,
      data?: { liveEvent?: boolean }
    ) => {
      const isLive = !toStartOfTimeline && data?.liveEvent === true;
      if (shouldPlay(event, room, isLive)) play();
    };

    const handleDecrypted = (event: MatrixEvent) => {
      const ts = event.getTs();
      if (ts && Date.now() - ts > DECRYPTED_MAX_AGE_MS) return;
      const roomId = event.getRoomId();
      const room = roomId ? matrixClient.getRoom(roomId) ?? undefined : undefined;
      if (shouldPlay(event, room, true)) play();
    };

    matrixClient.on(RoomEvent.Timeline, handleTimeline);
    matrixClient.on(MatrixEventEvent.Decrypted, handleDecrypted);

    return () => {
      matrixClient.off(RoomEvent.Timeline, handleTimeline);
      matrixClient.off(MatrixEventEvent.Decrypted, handleDecrypted);
    };
  }, [matrixClient]);
};

export default useSoundNotifications;
